/**
 * Toast Notification State & Timer Manager.
 *
 * Encapsulates self-clearing toast banner state and timer lifecycle.
 */

export interface ToastOptions {
  defaultDurationMs?: number;
  setTimeoutFn?: (handler: () => void, timeout?: number) => any;
  clearTimeoutFn?: (id: any) => void;
  onStateChange?: (state: ToastState) => void;
}

export interface ToastState {
  message: string;
  visible: boolean;
}

export interface ToastManager {
  show: (message: string, durationMs?: number) => ToastState;
  hide: () => ToastState;
  getState: () => ToastState;
}

/**
 * Creates a toast manager instance with auto-dismiss timer.
 */
export function createToastManager(options: ToastOptions = {}): ToastManager {
  const defaultDurationMs = options.defaultDurationMs ?? 4000;
  const setTimer = options.setTimeoutFn || setTimeout;
  const clearTimer = options.clearTimeoutFn || clearTimeout;
  const notify = options.onStateChange || (() => {});

  let state: ToastState = { message: "", visible: false };
  let timerId: any = null;

  function hide(): ToastState {
    if (timerId) {
      clearTimer(timerId);
      timerId = null;
    }
    state = { ...state, visible: false };
    notify(state);
    return state;
  }

  function show(message: string, durationMs?: number): ToastState {
    if (timerId) {
      clearTimer(timerId);
      timerId = null;
    }

    state = { message: message || "", visible: true };
    notify(state);

    const ms = durationMs !== undefined ? durationMs : defaultDurationMs;
    if (ms > 0) {
      timerId = setTimer(() => {
        hide();
      }, ms);
    }

    return state;
  }

  function getState(): ToastState {
    return state;
  }

  return {
    show,
    hide,
    getState,
  };
}
